/**
 * @fileoverview StructuredDiffCard — Apply/Reject UI for AI-proposed mutations
 *
 * Rendered above the chat input via `ChatSheet.inputBanner` (or the
 * equivalent slot in `UniversalChatPanel`). One card per pending
 * proposal — when multiple proposals are staged in a turn (e.g. agent
 * patches three fields on an asset and one on a contact) they stack.
 *
 * UX mirrors the precedent template chat Apply/Reject card but adapts
 * to structured field-level diffs instead of an HTML diff. The before
 * and after values are rendered side-by-side; fields that are NEW
 * (no before) get a "New" badge.
 *
 * Used by:
 * - `UniversalChatPanel` — wires `listPendingProposalsForThread` and
 *   the apply/reject mutations.
 */

import { useState, useCallback, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  CheckCircle2,
  XCircle,
  ArrowRight,
  Loader2,
  Sparkles,
  Expand,
  Link2,
  Unlink2,
} from "lucide-react";
import DOMPurify from "dompurify";
import { cn } from "@/utils/misc";
import { diffHtml } from "@/utils/precedents/htmlDiff";
import { humanizeFieldKey } from "@cvx/lib/fieldLabels";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PendingProposal {
  _id: Id<"agentProposals">;
  kind:
    | "entity_patch"
    | "entity_insert"
    | "entity_delete"
    | "canvas_patch"
    | "record_link"
    | "record_unlink";
  target: {
    table: string;
    id?: string;
  };
  before?: Record<string, unknown>;
  after: Record<string, unknown>;
  summary: string;
  createdAt: number;
  jobId?: Id<"jobs">;
  scope: "workspace" | "job";
}

interface StructuredDiffCardProps {
  proposal: PendingProposal;
  /** Optional callback after a successful Apply (so the parent can scroll). */
  onApplied?: () => void;
  className?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const FRIENDLY_TABLE_LABEL: Record<string, string> = {
  assetEntries: "Asset",
  jobContacts: "Contact",
  relatedPartyEntries: "Related party",
  liabilityEntries: "Liability",
  securityInterestEntries: "Security interest",
  loanEntries: "Loan",
  employeeEntries: "Employee",
  atoSroEntries: "ATO/SRO record",
  boardReportFindings: "Board finding",
  financialPeriodEntries: "Financial period",
  financialIndicatorEntries: "Solvency indicator",
  canvasDocuments: "Canvas document",
  recordLinks: "Link",
  jobKeyDates: "Task",
};

function formatValue(value: unknown): string {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString();
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StructuredDiffCard({
  proposal,
  onApplied,
  className,
}: StructuredDiffCardProps) {
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const { mutateAsync: applyProposal, isPending: isApplying } = useMutation({
    mutationFn: useConvexMutation(
      api.agents.universal.proposals.applyAgentProposal,
    ),
  });
  const { mutateAsync: rejectProposal, isPending: isRejecting } = useMutation({
    mutationFn: useConvexMutation(
      api.agents.universal.proposals.rejectAgentProposal,
    ),
  });

  const handleApply = useCallback(async () => {
    setError(null);
    try {
      await applyProposal({ proposalId: proposal._id });
      onApplied?.();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to apply the change.";
      setError(message);
    }
  }, [applyProposal, proposal._id, onApplied]);

  const handleReject = useCallback(async () => {
    setError(null);
    try {
      await rejectProposal({ proposalId: proposal._id });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to reject the change.";
      setError(message);
    }
  }, [rejectProposal, proposal._id]);

  const isBusy = isApplying || isRejecting;
  const tableLabel =
    FRIENDLY_TABLE_LABEL[proposal.target.table] ?? proposal.target.table;

  const isCanvas = proposal.kind === "canvas_patch";
  const isRecordLink =
    proposal.kind === "record_link" || proposal.kind === "record_unlink";
  const isUnlink = proposal.kind === "record_unlink";
  // Header eyebrow: link/unlink proposals describe a relationship, not a
  // table change, so they get their own label.
  const headerLabel = isRecordLink
    ? `${isUnlink ? "Unlink" : "Link"} proposed by Morgan`
    : `${tableLabel} change proposed by Morgan`;

  // Canvas patches diff full HTML bodies via the existing `htmldiff-js`
  // wrapper. Entity patches render a field-level table. The proposed HTML is
  // agent (LLM) output that has NOT yet round-tripped the TipTap schema, so
  // sanitise the diff markup before it is injected below. DOMPurify keeps
  // `<ins>`/`<del>` and `data-*` attributes, so the diff styling survives.
  const canvasDiffMarkup = useMemo(() => {
    if (!isCanvas) return null;
    const beforeHtml = (proposal.before?.html as string | undefined) ?? "";
    const afterHtml = (proposal.after.html as string | undefined) ?? "";
    return DOMPurify.sanitize(diffHtml(beforeHtml, afterHtml));
  }, [isCanvas, proposal.before, proposal.after]);

  // Derive the list of fields to display from `after`. Each row pairs
  // `before[field]` (which may be undefined) with `after[field]`.
  const fields = Object.keys(proposal.after);

  return (
    <div
      className={cn(
        "mx-3 my-2 rounded-lg border border-amber-500/30 bg-amber-500/5 shadow-sm",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-2.5 px-3 pb-2 pt-3">
        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <Sparkles className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
            {headerLabel}
          </p>
          <p className="text-sm font-medium text-foreground">
            {proposal.summary}
          </p>
        </div>
      </div>

      {/* Relationship (link / unlink) — the summary already names both
          records; show a compact icon row so the card doesn't look empty. */}
      {isRecordLink && (
        <div className="mx-3 mb-2 flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
          {isUnlink ? (
            <Unlink2 className="h-3.5 w-3.5 shrink-0 text-red-500" />
          ) : (
            <Link2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
          )}
          <span>
            {isUnlink
              ? "Removes this link and recalculates the affected secured flags and owed-amount totals."
              : "Creates this link and recalculates the affected secured flags and owed-amount totals."}
          </span>
        </div>
      )}

      {/* Canvas HTML diff */}
      {isCanvas && canvasDiffMarkup !== null && (
        <>
          <div className="relative mx-3 mb-2 max-h-[280px] overflow-y-auto rounded-md border border-border bg-background p-3">
            <div
              className="html-diff prose prose-sm max-w-none dark:prose-invert"
              // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- canvasDiffMarkup is DOMPurify.sanitize'd in the memo above (agent-proposed HTML)
              dangerouslySetInnerHTML={{ __html: canvasDiffMarkup }}
            />
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-foreground"
              title="Expand preview"
            >
              <Expand className="h-3.5 w-3.5" />
            </button>
          </div>
          <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
            <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col overflow-hidden">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-sm">
                  <Sparkles className="h-4 w-4 text-primary" />
                  {proposal.summary}
                </DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto rounded-md border border-border bg-background p-4">
                <div
                  className="html-diff prose prose-sm max-w-none dark:prose-invert"
                  // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- canvasDiffMarkup is DOMPurify.sanitize'd in the memo above (agent-proposed HTML)
                  dangerouslySetInnerHTML={{ __html: canvasDiffMarkup }}
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReject}
                  disabled={isBusy}
                  className="h-8 text-xs"
                >
                  {isRejecting ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <XCircle className="h-3 w-3" />
                  )}
                  Reject
                </Button>
                <Button
                  size="sm"
                  onClick={handleApply}
                  disabled={isBusy}
                  className="h-8 gap-1.5 text-xs"
                >
                  {isApplying ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3 w-3" />
                  )}
                  Apply
                  <ArrowRight className="h-3 w-3" />
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Entity field diff table */}
      {!isCanvas && !isRecordLink && (
        <div className="mx-3 mb-2 overflow-hidden rounded-md border border-border bg-background">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground/60">
              <tr>
                <th className="px-3 py-1.5 text-left font-medium">Field</th>
                <th className="px-3 py-1.5 text-left font-medium">Before</th>
                <th className="px-3 py-1.5 text-left font-medium">After</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field) => {
                const label = humanizeFieldKey(field);
                const beforeVal = proposal.before?.[field];
                const afterVal = proposal.after[field];
                const isNew =
                  beforeVal === undefined ||
                  beforeVal === null ||
                  beforeVal === "";
                return (
                  <tr key={field} className="border-t border-border/70">
                    <td className="px-3 py-1.5 align-top font-medium text-muted-foreground">
                      {label}
                    </td>
                    <td className="px-3 py-1.5 align-top">
                      {isNew ? (
                        <Badge color="gray" variant="soft" size="sm">
                          New
                        </Badge>
                      ) : (
                        <Badge
                          color="red"
                          variant="soft"
                          size="sm"
                          className="whitespace-normal break-words line-through [overflow-wrap:anywhere]"
                        >
                          {formatValue(beforeVal)}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-1.5 align-top">
                      <Badge
                        color="emerald"
                        variant="soft"
                        size="sm"
                        className="whitespace-normal break-words [overflow-wrap:anywhere]"
                      >
                        {formatValue(afterVal)}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Error */}
      {error && <p className="px-3 pb-1 text-xs text-destructive">{error}</p>}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 px-3 pb-3 pt-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleReject}
          disabled={isBusy}
          className="h-7 gap-1.5 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950/40 dark:hover:text-red-300"
        >
          {isRejecting ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <XCircle className="h-3 w-3" />
          )}
          Reject
        </Button>
        <Button
          size="sm"
          onClick={handleApply}
          disabled={isBusy}
          className="h-7 gap-1.5 text-xs"
        >
          {isApplying ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <CheckCircle2 className="h-3 w-3" />
          )}
          Apply
          <ArrowRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProposalReviewStack — renders the full pending-proposal queue for a
// thread with a sticky bulk action bar (Apply all / Reject all) above the
// individual diff cards. Used by SummaryChatSheet and UniversalChatPanel.
// ---------------------------------------------------------------------------

/**
 * Bulk operations are scoped either to a single chat thread (workspace
 * agent — one thread per session) or to a whole summarisation task
 * (summary chats — proposals may be spread across several of the user's
 * chat threads for the same agent summary).
 */
export type ProposalScope =
  | { kind: "thread"; threadId: string }
  | { kind: "task"; summarizationTaskId: Id<"summarizationTasks"> };

interface ProposalReviewStackProps {
  proposals: PendingProposal[];
  /** What the bulk Apply all / Reject all act on. */
  scope: ProposalScope;
  className?: string;
}

export function ProposalReviewStack({
  proposals,
  scope,
  className,
}: ProposalReviewStackProps) {
  const [bulkError, setBulkError] = useState<string | null>(null);

  const { mutateAsync: applyAllThread, isPending: applyingThread } =
    useMutation({
      mutationFn: useConvexMutation(
        api.agents.universal.proposals.applyAllPendingProposalsForThread,
      ),
    });
  const { mutateAsync: rejectAllThread, isPending: rejectingThread } =
    useMutation({
      mutationFn: useConvexMutation(
        api.agents.universal.proposals.rejectAllPendingProposalsForThread,
      ),
    });
  const { mutateAsync: applyAllTask, isPending: applyingTask } = useMutation({
    mutationFn: useConvexMutation(
      api.agents.universal.proposals.applyAllPendingProposalsForTask,
    ),
  });
  const { mutateAsync: rejectAllTask, isPending: rejectingTask } = useMutation({
    mutationFn: useConvexMutation(
      api.agents.universal.proposals.rejectAllPendingProposalsForTask,
    ),
  });

  const isBulkBusy =
    applyingThread || rejectingThread || applyingTask || rejectingTask;

  const handleApplyAll = useCallback(async () => {
    setBulkError(null);
    try {
      const result =
        scope.kind === "thread"
          ? await applyAllThread({ threadId: scope.threadId })
          : await applyAllTask({
              summarizationTaskId: scope.summarizationTaskId,
            });
      if (result.failed > 0) {
        setBulkError(
          `${result.applied} applied, ${result.failed} failed.${
            result.errors[0] ? ` First error: ${result.errors[0]}` : ""
          }`,
        );
      }
    } catch (err) {
      setBulkError(
        err instanceof Error ? err.message : "Unable to apply all changes.",
      );
    }
  }, [scope, applyAllThread, applyAllTask]);

  const handleRejectAll = useCallback(async () => {
    setBulkError(null);
    try {
      if (scope.kind === "thread") {
        await rejectAllThread({ threadId: scope.threadId });
      } else {
        await rejectAllTask({ summarizationTaskId: scope.summarizationTaskId });
      }
    } catch (err) {
      setBulkError(
        err instanceof Error ? err.message : "Unable to reject all changes.",
      );
    }
  }, [scope, rejectAllThread, rejectAllTask]);

  if (proposals.length === 0) return null;

  const showBulkBar = proposals.length > 1;

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Bulk action bar — only when more than one change is staged. */}
      {showBulkBar && (
        <div className="mx-3 mt-2 flex items-center justify-between gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
          <div className="flex min-w-0 items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-xs font-medium text-foreground">
              {proposals.length} changes proposed by Morgan
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRejectAll}
              disabled={isBulkBusy}
              className="h-7 gap-1.5 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950/40 dark:hover:text-red-300"
            >
              {rejectingThread || rejectingTask ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <XCircle className="h-3 w-3" />
              )}
              Reject all
            </Button>
            <Button
              size="sm"
              onClick={handleApplyAll}
              disabled={isBulkBusy}
              className="h-7 gap-1.5 text-xs"
            >
              {applyingThread || applyingTask ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3 w-3" />
              )}
              Apply all
            </Button>
          </div>
        </div>
      )}

      {bulkError && (
        <p className="mx-3 mt-1.5 text-xs text-destructive">{bulkError}</p>
      )}

      {proposals.map((proposal) => (
        <StructuredDiffCard key={proposal._id} proposal={proposal} />
      ))}
    </div>
  );
}
